// API layer unificado do core moderno (Fase 2.3 do MODERNIZACAO.md).
// Mesmas semânticas do legado src/js/api.js — envelope {ok, data, error},
// deduplicação de GETs em voo, cache opt-in por endpoint e invalidação por
// entidade em mutações — mais interceptors (onRequest/onResponse/onError).
// Autenticação: cookie httpOnly por padrão; enquanto a flag cookieAuth não
// estiver 100% ativa, envia x-token quando houver token no localStorage.

import * as Session from './session'
import { joinPath } from './utils'
import { isEnabled } from '../config/flags'

interface Envelope<T> {
  ok: boolean
  data?: T
  error?: string
}

export interface RequestConfig {
  method: string
  path: string
  url: string
  headers: Record<string, string>
  body?: BodyInit
}

export type RequestInterceptor = (config: RequestConfig) => void
export type ResponseInterceptor = (response: Response, config: RequestConfig) => void
export type ErrorInterceptor = (error: unknown, config: RequestConfig) => void

export class ApiError extends Error {
  status: number | null

  constructor(message: string, status: number | null = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * Cache opt-in por endpoint, com TTL em milissegundos. Registrar APENAS
 * dados de referência estáveis (ver MODERNIZACAO.md 1.4). NUNCA registrar
 * disponibilidade de vagas/horários.
 */
export const cacheTTL: Record<string, number> = {}

const cache = new Map<string, { data: unknown; expires: number }>()
const inflight = new Map<string, Promise<unknown>>()

export const onRequest: RequestInterceptor[] = []
export const onResponse: ResponseInterceptor[] = []
export const onError: ErrorInterceptor[] = []

function normalizePath(path: string): string {
  return path.replace(/^\/+/, '')
}

function fullPath(...args: string[]): string {
  const municipio = Session.get<{ apelido?: string }>('municipio')
  const apelido = municipio && typeof municipio === 'object' ? (municipio.apelido ?? '') : ''
  return joinPath('/api', apelido, ...args)
}

function cacheKey(path: string, params: Record<string, unknown> = {}): string {
  return normalizePath(path) + '?' + JSON.stringify(params)
}

export function invalidateCache(pathPrefix: string): void {
  const prefix = normalizePath(pathPrefix)
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key)
    }
  }
}

/**
 * As rotas seguem o padrão consorcio/{entidade}/{acao}: uma mutação
 * invalida o cache da entidade inteira (path sem o último segmento).
 */
function invalidateRecurso(path: string): void {
  const partes = normalizePath(path).split('/')
  if (partes.length > 1) {
    partes.pop()
  }
  invalidateCache(partes.join('/'))
}

function authHeaders(): Record<string, string> {
  if (isEnabled('cookieAuth')) {
    return {}
  }
  const token = Session.get('token')
  return typeof token === 'string' && token ? { 'x-token': token } : {}
}

async function request<T>(config: RequestConfig): Promise<T> {
  for (const interceptor of onRequest) {
    interceptor(config)
  }

  let response: Response
  try {
    response = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
      body: config.body,
      credentials: 'same-origin',
    })
  } catch (err) {
    const error = new ApiError('Falha de rede ao acessar a API')
    for (const interceptor of onError) {
      interceptor(err, config)
    }
    throw error
  }

  for (const interceptor of onResponse) {
    interceptor(response, config)
  }

  let json: Envelope<T> | null = null
  try {
    json = (await response.json()) as Envelope<T>
  } catch {
    json = null
  }

  if (!response.ok || !json || !json.ok) {
    const error = new ApiError(json?.error ?? `Erro ${response.status} na API`, response.status)
    for (const interceptor of onError) {
      interceptor(error, config)
    }
    throw error
  }

  return json.data as T
}

async function get<T>(path: string, params: Record<string, string> = {}): Promise<T> {
  const key = cacheKey(path, params)
  const ttl = cacheTTL[normalizePath(path)]

  if (ttl) {
    const hit = cache.get(key)
    if (hit && hit.expires > Date.now()) {
      return hit.data as T
    }
  }

  const emVoo = inflight.get(key)
  if (emVoo) {
    return emVoo as Promise<T>
  }

  const query = new URLSearchParams(params).toString()
  const url = fullPath(path) + (query ? '?' + query : '')

  const compartilhada = request<T>({
    method: 'GET',
    path,
    url,
    headers: authHeaders(),
  }).then(
    (dados) => {
      if (ttl) {
        cache.set(key, { data: dados, expires: Date.now() + ttl })
      }
      inflight.delete(key)
      return dados
    },
    (err: unknown) => {
      inflight.delete(key)
      throw err
    }
  )

  inflight.set(key, compartilhada)
  return compartilhada
}

async function post<T>(path: string, data: unknown = {}): Promise<T> {
  const dados = await request<T>({
    method: 'POST',
    path,
    url: fullPath(path),
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  })
  invalidateRecurso(path)
  return dados
}

async function del<T>(path: string, id: string | number): Promise<T> {
  const dados = await request<T>({
    method: 'DELETE',
    path,
    url: fullPath(path, String(id)),
    headers: authHeaders(),
  })
  invalidateRecurso(path)
  return dados
}

export const api = {
  get,
  post,
  delete: del,
  invalidateCache,
  onRequest,
  onResponse,
  onError,
}
